import { checkAccessProofField } from './form-access-requests';
import { checkSentFormField } from './sent-forms';
import { sanitizeIssueRichText } from '../utils/issue-rich-text';
import { isAllowedHref } from '../website-schema/richtext-sanitizer';
import { FORM_DEFAULT_TODAY, FORM_DEFAULT_TODAY_TYPES } from './form-defaults';
import {
	FORM_FILE_MAX_FILES,
	FORM_FILE_MAX_MB,
	FORM_FILE_MIN_FILES,
	FORM_FILE_MIN_MB,
} from './form-files';
import {
	FORM_LAYOUT_CHOICE_TYPES,
	FORM_LAYOUT_COLUMN_COUNTS,
	FORM_LAYOUT_ID_PATTERN,
	FORM_LAYOUT_LIMITS,
	FORM_LAYOUT_TEXT_STYLES,
	FORM_LAYOUT_VERSION,
	type FormLayoutCellModel,
	type FormLayoutColumnCount,
	type FormLayoutModel,
} from './form-layout';
import { checkFormFieldShowWhen } from './form-show-when';
import {
	FORM_CHOICE_SOURCE_MAX_ENTRIES,
	FORM_TEAM_MEMBER_TYPE,
	normalizeFormChoiceSource,
} from './form-team-member';
import {
	FORM_CONSENT_PURPOSES,
	FORM_FIELD_IDENTITIES,
	FORM_FILE_TYPE_GROUPS,
	FORM_FIELD_KEY_PATTERN,
	FORM_FIELD_TYPES,
	FORM_KEY_PATTERN,
	FORM_RULE_TYPES,
	FORM_TERMS_BODY_MAX_LENGTH,
	FORM_TERMS_LINK_TEXT_MAX_LENGTH,
	type FormAnswerValue,
	type FormFieldDefinitionModel,
	type FormRuleModel,
} from './form-types';
import {
	FORM_FIELD_DEFAULT_MAX_LENGTH,
	FORM_PATTERN_MAX_LENGTH,
} from './form-validator';

/**
 * Checks on a form DEFINITION, run when staff save a form (crm-api refuses an invalid
 * definition; the settings screen shows the same problems as the admin edits). Kept next
 * to the validator so the rules a definition may contain and the rules the validator
 * enforces cannot drift apart.
 */

export interface FormDefinitionProblemModel {
	/** `fields[2].validationRules.pattern`, `rules[0].fieldKeys`, `formKey` … */
	path: string;
	message: string;
}

const TEXT_LENGTH_TYPES = new Set<string>([
	'text',
	'textarea',
	'hidden',
	'email',
	'url',
	'phone',
]);
const RANGE_NUMBER_TYPES = new Set<string>(['number', 'currency']);
const CHOICE_TYPES = new Set<string>(['select', 'multiselect']);

/**
 * PD-16691 — questions that never say who the submitter is, or start filled in. PD-16713:
 * a file question too; its answer is the files the visitor attaches.
 */
const NO_IDENTITY_TYPES = new Set<string>([
	'signature',
	'teamMember',
	'file',
	'table',
	'paymentMethod',
	// PD-17590 — a proof block has no answer at all.
	'accessProof',
]);
const NO_DEFAULT_TYPES = new Set<string>([
	'signature',
	'teamMember',
	'file',
	'accessProof',
]);
const FIELD_TYPE_WORDS: Readonly<Record<string, string>> = {
	signature: 'A signature',
	teamMember: 'An account manager question',
	file: 'A file upload question',
	table: 'A table',
	paymentMethod: 'A payment method question',
	accessProof: 'A proof block',
};

export function checkFormKey(
	formKey: string,
): Array<FormDefinitionProblemModel> {
	return FORM_KEY_PATTERN.test(formKey)
		? []
		: [
				{
					path: 'formKey',
					message:
						'Use 1 to 32 lowercase letters, digits, hyphens or underscores (the key is sent to the spam check, which allows 32 characters)',
				},
			];
}

export function checkFormDefinition(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	rules: ReadonlyArray<FormRuleModel>,
	layout: FormLayoutModel | null = null,
): Array<FormDefinitionProblemModel> {
	const problems: Array<FormDefinitionProblemModel> = [];
	const seenKeys = new Set<string>();

	fields.forEach((field, index) => {
		const at = `fields[${index}]`;

		if (!FORM_FIELD_KEY_PATTERN.test(field.fieldKey)) {
			problems.push({
				path: `${at}.fieldKey`,
				message:
					'A field key starts with a letter and holds only letters, digits and underscores (at most 60)',
			});
		} else if (seenKeys.has(field.fieldKey)) {
			problems.push({
				path: `${at}.fieldKey`,
				message: `The key ${field.fieldKey} is used by more than one field`,
			});
		}
		seenKeys.add(field.fieldKey);

		if (
			!(FORM_FIELD_TYPES as ReadonlyArray<string>).includes(field.fieldType)
		) {
			problems.push({
				path: `${at}.fieldType`,
				message: `Unknown field type ${field.fieldType}`,
			});
		}

		if (field.label.trim().length === 0) {
			problems.push({ path: `${at}.label`, message: 'A field needs a label' });
		}

		if (
			field.identity != null &&
			!(FORM_FIELD_IDENTITIES as ReadonlyArray<string>).includes(field.identity)
		) {
			problems.push({
				path: `${at}.identity`,
				message: `Unknown identity ${field.identity}`,
			});
		} else if (
			field.identity != null &&
			NO_IDENTITY_TYPES.has(field.fieldType)
		) {
			// Matching and a new lead read identities; a drawn signature, or the person on
			// the tenant's own team the visitor picked, tells them nothing about the sender.
			problems.push({
				path: `${at}.identity`,
				message: `${FIELD_TYPE_WORDS[field.fieldType] ?? field.fieldType} says nothing about who sent the form`,
			});
		}

		if (field.consentPurpose != null) {
			if (field.fieldType !== 'consent') {
				problems.push({
					path: `${at}.consentPurpose`,
					message: 'Only a consent field has a consent purpose',
				});
			} else if (
				!(FORM_CONSENT_PURPOSES as ReadonlyArray<string>).includes(
					field.consentPurpose,
				)
			) {
				problems.push({
					path: `${at}.consentPurpose`,
					message: `Unknown consent purpose ${field.consentPurpose}`,
				});
			}
		}

		problems.push(...checkFormFieldTerms(field, at));
		problems.push(...checkFormFieldDefault(field, at));
		problems.push(...checkFormFieldChoiceSource(field, at));
		// PD-16730 — Show only when: an earlier choice question, and answers it offers.
		problems.push(...checkFormFieldShowWhen(fields, index));

		// PD-13768 — a dropdown whose choices the server works out (term lengths, billing
		// terms) has none of its own.
		if (
			CHOICE_TYPES.has(field.fieldType) &&
			field.fieldOptions?.choices == null
		) {
			problems.push(...checkOptions(field, at));
		}

		problems.push(...checkValidationRules(field, at));
		// PD-13768 — sent-form settings: source, customer access, write-back, options.
		problems.push(...checkSentFormField(field, at));
		// PD-17590 — an access request's proof block: its settings and what it relies on.
		problems.push(...checkAccessProofField(fields, index, at));
	});

	rules.forEach((rule, index) => {
		problems.push(...checkRule(rule, `rules[${index}]`, seenKeys));
	});

	if (layout != null) {
		problems.push(...checkFormLayout(layout, fields));
	}

	return problems;
}

/**
 * Is this an address a consent field may link its terms document to? An absolute https
 * address only: the link opens in a new tab from a public page, so a plain http page, an
 * internal path (which would resolve against whichever site shows the form), `mailto:`
 * and `tel:` are all refused. The shared link rules still apply first, which refuse
 * `javascript:`, control characters and backslash tricks.
 */
export function isFormTermsUrl(url: string): boolean {
	if (
		!isAllowedHref(url, { isHttpAllowed: false }) ||
		!url.toLowerCase().startsWith('https://')
	) {
		return false;
	}
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:' && parsed.hostname.length > 0;
	} catch {
		return false;
	}
}

/**
 * PD-16691 — the terms a consent field shows under its checkbox: a formatted body, a
 * link to the full document, and that link's wording. Exported so the field editor
 * shows the same problems the server would refuse. Values are read as stored (the server
 * sanitizes the body and trims the rest before this runs); a missing key, as in a version
 * written before terms existed, counts as empty.
 */
export function checkFormFieldTerms(
	field: Pick<
		FormFieldDefinitionModel,
		'fieldType' | 'termsBody' | 'termsUrl' | 'termsLinkText'
	>,
	at: string,
): Array<FormDefinitionProblemModel> {
	const termsBody = field.termsBody ?? null;
	const termsUrl = field.termsUrl ?? null;
	const termsLinkText = field.termsLinkText ?? null;

	if (field.fieldType !== 'consent') {
		return [
			{ key: 'termsBody', value: termsBody },
			{ key: 'termsUrl', value: termsUrl },
			{ key: 'termsLinkText', value: termsLinkText },
		]
			.filter((entry) => entry.value != null)
			.map((entry) => ({
				path: `${at}.${entry.key}`,
				message: 'Only a consent field shows terms',
			}));
	}

	const problems: Array<FormDefinitionProblemModel> = [];

	if (
		termsBody != null &&
		sanitizeIssueRichText(termsBody).length > FORM_TERMS_BODY_MAX_LENGTH
	) {
		problems.push({
			path: `${at}.termsBody`,
			message: `The terms are at most ${FORM_TERMS_BODY_MAX_LENGTH.toLocaleString('en-US')} characters, formatting included`,
		});
	}

	if (termsUrl != null && !isFormTermsUrl(termsUrl)) {
		problems.push({
			path: `${at}.termsUrl`,
			message: 'Enter the full address of the document, starting with https://',
		});
	}

	if (termsLinkText != null) {
		if (termsLinkText.length > FORM_TERMS_LINK_TEXT_MAX_LENGTH) {
			problems.push({
				path: `${at}.termsLinkText`,
				message: `The link wording is at most ${FORM_TERMS_LINK_TEXT_MAX_LENGTH} characters`,
			});
		} else if (termsUrl == null) {
			problems.push({
				path: `${at}.termsLinkText`,
				message: 'Add a document link, or clear the link wording',
			});
		}
	}

	return problems;
}

/**
 * PD-16691 — a question's default value. "Default to today" (FORM_DEFAULT_TODAY) is only
 * meaningful for a date or date-and-time question; anywhere else the page would show the
 * token itself as the answer. A signature is drawn by the visitor, so it has no default.
 * Exported so the field editor shows the same problem.
 */
export function checkFormFieldDefault(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'defaultValue'>,
	at: string,
): Array<FormDefinitionProblemModel> {
	if (field.defaultValue != null && NO_DEFAULT_TYPES.has(field.fieldType)) {
		return [
			{
				path: `${at}.defaultValue`,
				message: `${FIELD_TYPE_WORDS[field.fieldType] ?? field.fieldType} has no default`,
			},
		];
	}
	if (
		field.defaultValue === FORM_DEFAULT_TODAY &&
		!FORM_DEFAULT_TODAY_TYPES.includes(field.fieldType)
	) {
		return [
			{
				path: `${at}.defaultValue`,
				message:
					'Default to today applies to date and date-and-time questions only',
			},
		];
	}
	return [];
}

/**
 * PD-16691 — an account-manager question's list of people: at least one role or person,
 * and no fixed choices of its own (its choices are worked out from the list each time the
 * form is drawn). No other kind of question has a list. Exported so the field editor
 * shows the same problem.
 */
export function checkFormFieldChoiceSource(
	field: Pick<
		FormFieldDefinitionModel,
		'fieldType' | 'choiceSource' | 'selectOptions'
	>,
	at: string,
): Array<FormDefinitionProblemModel> {
	const raw = field.choiceSource ?? null;
	if (field.fieldType !== FORM_TEAM_MEMBER_TYPE) {
		return raw != null
			? [
					{
						path: `${at}.choiceSource`,
						message: 'Only an account manager question lists people',
					},
				]
			: [];
	}

	const problems: Array<FormDefinitionProblemModel> = [];
	const source = normalizeFormChoiceSource(raw);
	const count = (source?.roles.length ?? 0) + (source?.users.length ?? 0);
	if (count === 0) {
		problems.push({
			path: `${at}.choiceSource`,
			message: 'Choose at least one role or person to list',
		});
	} else if (count > FORM_CHOICE_SOURCE_MAX_ENTRIES) {
		problems.push({
			path: `${at}.choiceSource`,
			message: `Name at most ${FORM_CHOICE_SOURCE_MAX_ENTRIES} roles and people`,
		});
	}
	if ((field.selectOptions ?? []).length > 0) {
		problems.push({
			path: `${at}.selectOptions`,
			message:
				'An account manager question lists the people you choose; it has no choices of its own',
		});
	}
	return problems;
}

function checkOptions(
	field: FormFieldDefinitionModel,
	at: string,
): Array<FormDefinitionProblemModel> {
	const options = field.selectOptions ?? [];
	if (options.length === 0) {
		return [
			{ path: `${at}.selectOptions`, message: 'A choice field needs options' },
		];
	}

	const problems: Array<FormDefinitionProblemModel> = [];
	const values = new Set<string>();
	options.forEach((option, optionIndex) => {
		const value = option.value.trim();
		if (value.length === 0) {
			problems.push({
				path: `${at}.selectOptions[${optionIndex}].value`,
				message: 'An option needs a value',
			});
		} else if (value.length > FORM_FIELD_DEFAULT_MAX_LENGTH.select) {
			problems.push({
				path: `${at}.selectOptions[${optionIndex}].value`,
				message: `An option value is at most ${FORM_FIELD_DEFAULT_MAX_LENGTH.select} characters`,
			});
		} else if (values.has(value)) {
			problems.push({
				path: `${at}.selectOptions[${optionIndex}].value`,
				message: `The option value ${value} appears more than once`,
			});
		}
		values.add(value);
	});
	return problems;
}

function isNonNegativeInteger(value: unknown): boolean {
	return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function checkValidationRules(
	field: FormFieldDefinitionModel,
	at: string,
): Array<FormDefinitionProblemModel> {
	const rules = field.validationRules;
	if (rules == null) {
		return [];
	}

	const problems: Array<FormDefinitionProblemModel> = [];
	const path = `${at}.validationRules`;

	if (rules.minLength != null || rules.maxLength != null) {
		if (!TEXT_LENGTH_TYPES.has(field.fieldType)) {
			problems.push({
				path,
				message: 'Minimum and maximum length apply to text fields only',
			});
		}
		if (rules.minLength != null && !isNonNegativeInteger(rules.minLength)) {
			problems.push({
				path: `${path}.minLength`,
				message: 'Minimum length is a whole number',
			});
		}
		if (rules.maxLength != null && !isNonNegativeInteger(rules.maxLength)) {
			problems.push({
				path: `${path}.maxLength`,
				message: 'Maximum length is a whole number',
			});
		}
		if (
			typeof rules.minLength === 'number' &&
			typeof rules.maxLength === 'number' &&
			rules.minLength > rules.maxLength
		) {
			problems.push({
				path,
				message: 'Minimum length is larger than maximum length',
			});
		}
	}

	if (rules.pattern != null && rules.pattern.length > 0) {
		if (!TEXT_LENGTH_TYPES.has(field.fieldType)) {
			problems.push({
				path: `${path}.pattern`,
				message: 'A format rule applies to text fields only',
			});
		} else if (rules.pattern.length > FORM_PATTERN_MAX_LENGTH) {
			problems.push({
				path: `${path}.pattern`,
				message: `A format rule is at most ${FORM_PATTERN_MAX_LENGTH} characters`,
			});
		} else {
			try {
				new RegExp(`^(?:${rules.pattern})$`);
			} catch {
				problems.push({
					path: `${path}.pattern`,
					message: 'The format rule is not a valid regular expression',
				});
			}
		}
	}

	if (rules.min != null || rules.max != null) {
		problems.push(...checkRange(field.fieldType, rules.min, rules.max, path));
	}

	problems.push(...checkFileRules(field, path));

	return problems;
}

/**
 * PD-16713 — a file question's limits: at least one kind of file, the largest file from
 * FORM_FILE_MIN_MB to FORM_FILE_MAX_MB megabytes, and the most files from
 * FORM_FILE_MIN_FILES to FORM_FILE_MAX_FILES, each in whole numbers. Only a file question
 * has them. Exported so the field editor shows the same problems.
 */
export function checkFormFileRules(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'validationRules'>,
	at: string,
): Array<FormDefinitionProblemModel> {
	return checkFileRules(field, `${at}.validationRules`);
}

function checkFileRules(
	field: Pick<FormFieldDefinitionModel, 'fieldType' | 'validationRules'>,
	path: string,
): Array<FormDefinitionProblemModel> {
	const rules = field.validationRules;
	if (rules == null) {
		return [];
	}
	const hasFileRules =
		rules.fileTypes != null ||
		rules.maxFileSizeMb != null ||
		rules.maxFiles != null;
	if (!hasFileRules) {
		return [];
	}
	if (field.fieldType !== 'file') {
		return [
			{
				path,
				message:
					'Kinds of file, the largest file and the most files apply to file upload questions only',
			},
		];
	}

	const problems: Array<FormDefinitionProblemModel> = [];
	if (rules.fileTypes != null) {
		// Read as unknown: a definition from a request can carry anything the type rules out.
		const types: unknown = rules.fileTypes;
		const known = FORM_FILE_TYPE_GROUPS as ReadonlyArray<unknown>;
		if (!Array.isArray(types) || types.length === 0) {
			problems.push({
				path: `${path}.fileTypes`,
				message: 'Choose at least one kind of file',
			});
		} else {
			const unknown = types.filter((type) => !known.includes(type));
			if (unknown.length > 0) {
				problems.push({
					path: `${path}.fileTypes`,
					message: `Unknown kind of file: ${unknown.map(String).join(', ')}`,
				});
			} else if (new Set(types).size !== types.length) {
				problems.push({
					path: `${path}.fileTypes`,
					message: 'A kind of file is listed more than once',
				});
			}
		}
	}
	if (
		rules.maxFileSizeMb != null &&
		!isWholeNumberIn(rules.maxFileSizeMb, FORM_FILE_MIN_MB, FORM_FILE_MAX_MB)
	) {
		problems.push({
			path: `${path}.maxFileSizeMb`,
			message: `The largest file is a whole number of megabytes from ${FORM_FILE_MIN_MB} to ${FORM_FILE_MAX_MB}`,
		});
	}
	if (
		rules.maxFiles != null &&
		!isWholeNumberIn(rules.maxFiles, FORM_FILE_MIN_FILES, FORM_FILE_MAX_FILES)
	) {
		problems.push({
			path: `${path}.maxFiles`,
			message: `The most files is a whole number from ${FORM_FILE_MIN_FILES} to ${FORM_FILE_MAX_FILES}`,
		});
	}
	return problems;
}

function isWholeNumberIn(value: unknown, min: number, max: number): boolean {
	return (
		typeof value === 'number' &&
		Number.isInteger(value) &&
		value >= min &&
		value <= max
	);
}

function checkRange(
	fieldType: string,
	min: number | string | undefined,
	max: number | string | undefined,
	path: string,
): Array<FormDefinitionProblemModel> {
	if (RANGE_NUMBER_TYPES.has(fieldType)) {
		const bad = [min, max].some(
			(bound) =>
				bound != null && (typeof bound !== 'number' || !Number.isFinite(bound)),
		);
		if (bad) {
			return [{ path, message: 'Minimum and maximum are numbers' }];
		}
	} else if (fieldType === 'date') {
		const bad = [min, max].some(
			(bound) =>
				bound != null &&
				(typeof bound !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(bound)),
		);
		if (bad) {
			return [{ path, message: 'Earliest and latest dates are YYYY-MM-DD' }];
		}
	} else {
		return [
			{
				path,
				message:
					'Minimum and maximum apply to number, currency and date fields only',
			},
		];
	}

	// Both bounds are now the same kind: numbers, or YYYY-MM-DD strings that compare as text.
	const inverted =
		(typeof min === 'number' && typeof max === 'number' && min > max) ||
		(typeof min === 'string' && typeof max === 'string' && min > max);
	if (inverted) {
		return [{ path, message: 'The minimum is larger than the maximum' }];
	}
	return [];
}

function checkRule(
	rule: FormRuleModel,
	at: string,
	fieldKeys: ReadonlySet<string>,
): Array<FormDefinitionProblemModel> {
	if (!(FORM_RULE_TYPES as ReadonlyArray<string>).includes(rule.rule)) {
		return [{ path: `${at}.rule`, message: `Unknown rule ${rule.rule}` }];
	}

	const distinct = new Set(rule.fieldKeys);
	if (distinct.size < 2) {
		return [
			{
				path: `${at}.fieldKeys`,
				message: '"At least one of" needs two or more different fields',
			},
		];
	}

	const missing = [...distinct].filter((key) => !fieldKeys.has(key));
	if (missing.length > 0) {
		return [
			{
				path: `${at}.fieldKeys`,
				message: `The rule names fields that are not on the form: ${missing.join(', ')}`,
			},
		];
	}

	return [];
}

function isLayoutColumnCount(value: unknown): value is FormLayoutColumnCount {
	return (FORM_LAYOUT_COLUMN_COUNTS as ReadonlyArray<unknown>).includes(value);
}

/**
 * PD-16687 — checks on a form's layout against the questions it arranges. A layout only
 * places questions and never changes what is asked, so each question is placed at most
 * once, and one it leaves out is still asked (drawn after the last section).
 */
export function checkFormLayout(
	layout: FormLayoutModel,
	fields: ReadonlyArray<
		Pick<FormFieldDefinitionModel, 'fieldKey' | 'fieldType'>
	>,
): Array<FormDefinitionProblemModel> {
	const problems: Array<FormDefinitionProblemModel> = [];
	// Read as unknown: a layout from a request can carry anything the type rules out.
	const version: unknown = layout.version;
	if (version !== FORM_LAYOUT_VERSION) {
		problems.push({
			path: 'layout.version',
			message: `Unknown layout version ${String(version)}`,
		});
	}

	const context: LayoutCheckContextModel = {
		problems,
		typeByKey: new Map(
			fields.map((field) => [field.fieldKey, field.fieldType]),
		),
		seenIds: new Set(),
		placed: new Set(),
	};

	layout.sections.forEach((section, sectionIndex) => {
		const at = `layout.sections[${sectionIndex}]`;
		checkLayoutId(section.id, `${at}.id`, context);
		if (
			section.title != null &&
			section.title.length > FORM_LAYOUT_LIMITS.sectionTitle
		) {
			problems.push({
				path: `${at}.title`,
				message: `A section title is at most ${FORM_LAYOUT_LIMITS.sectionTitle} characters`,
			});
		}
		if (
			section.description != null &&
			section.description.length > FORM_LAYOUT_LIMITS.sectionDescription
		) {
			problems.push({
				path: `${at}.description`,
				message: `A section description is at most ${FORM_LAYOUT_LIMITS.sectionDescription} characters`,
			});
		}

		section.rows.forEach((row, rowIndex) => {
			const rowAt = `${at}.rows[${rowIndex}]`;
			checkLayoutId(row.id, `${rowAt}.id`, context);
			const columns: unknown = row.columns;
			const columnsValid = isLayoutColumnCount(columns);
			if (!columnsValid) {
				problems.push({
					path: `${rowAt}.columns`,
					message: 'A row has 1, 2 or 3 columns',
				});
			}
			if (row.cells.length === 0) {
				problems.push({
					path: `${rowAt}.cells`,
					message: 'A row needs a question or some text',
				});
			}

			let taken = 0;
			row.cells.forEach((cell, cellIndex) => {
				const cellAt = `${rowAt}.cells[${cellIndex}]`;
				const span: unknown = cell.span ?? 1;
				if (isLayoutColumnCount(span)) {
					taken += span;
				} else {
					problems.push({
						path: `${cellAt}.span`,
						message: 'A question or text takes 1, 2 or 3 columns',
					});
				}
				checkLayoutCell(cell, cellAt, context);
			});
			if (columnsValid && taken > row.columns) {
				problems.push({
					path: `${rowAt}.cells`,
					message: `The questions and text in this row take ${taken} columns, but the row has ${row.columns}`,
				});
			}
		});
	});

	return problems;
}

interface LayoutCheckContextModel {
	problems: Array<FormDefinitionProblemModel>;
	typeByKey: ReadonlyMap<string, string>;
	seenIds: Set<string>;
	placed: Set<string>;
}

function checkLayoutId(
	id: unknown,
	path: string,
	context: LayoutCheckContextModel,
): void {
	if (typeof id !== 'string' || !FORM_LAYOUT_ID_PATTERN.test(id)) {
		context.problems.push({
			path,
			message:
				'A layout id holds only letters, digits, hyphens and underscores (at most 40)',
		});
	} else if (context.seenIds.has(id)) {
		context.problems.push({
			path,
			message: `The id ${id} is used more than once in the layout`,
		});
	} else {
		context.seenIds.add(id);
	}
}

function checkLayoutCell(
	cell: FormLayoutCellModel,
	at: string,
	context: LayoutCheckContextModel,
): void {
	const { problems } = context;
	// Read as a string: a cell from a request can name a kind the type rules out.
	const kind: string = cell.kind;
	if (kind !== 'field' && kind !== 'text') {
		problems.push({
			path: `${at}.kind`,
			message: 'A layout cell is a question or text',
		});
		return;
	}

	if (cell.kind === 'field') {
		const fieldType = context.typeByKey.get(cell.fieldKey);
		if (fieldType == null) {
			problems.push({
				path: `${at}.fieldKey`,
				message: `The layout places ${cell.fieldKey}, which is not a question on this form`,
			});
		} else if (fieldType === 'hidden') {
			problems.push({
				path: `${at}.fieldKey`,
				message: `${cell.fieldKey} is a hidden value, which is never shown`,
			});
		} else if (context.placed.has(cell.fieldKey)) {
			problems.push({
				path: `${at}.fieldKey`,
				message: `The question ${cell.fieldKey} is placed more than once`,
			});
		}
		context.placed.add(cell.fieldKey);

		if (cell.optionColumns != null) {
			if (fieldType != null && !FORM_LAYOUT_CHOICE_TYPES.includes(fieldType)) {
				problems.push({
					path: `${at}.optionColumns`,
					message:
						'Choice columns apply to dropdown, multiple-choice and account manager questions only',
				});
			} else if (!isLayoutColumnCount(cell.optionColumns)) {
				problems.push({
					path: `${at}.optionColumns`,
					message: 'Choices sit in 1, 2 or 3 columns',
				});
			}
		}
		return;
	}

	checkLayoutId(cell.id, `${at}.id`, context);
	if (
		!(FORM_LAYOUT_TEXT_STYLES as ReadonlyArray<string>).includes(cell.style)
	) {
		problems.push({
			path: `${at}.style`,
			message: `Unknown text style ${cell.style}`,
		});
	}
	if (cell.text.trim().length === 0) {
		problems.push({ path: `${at}.text`, message: 'A text block needs text' });
	} else if (cell.text.length > FORM_LAYOUT_LIMITS.text) {
		problems.push({
			path: `${at}.text`,
			message: `A text block is at most ${FORM_LAYOUT_LIMITS.text} characters`,
		});
	}
}

export interface FormSubmitterIdentityModel {
	name: string | null;
	email: string | null;
	phone: string | null;
	company: string | null;
}

/**
 * Who the submitter says they are, read from the fields' identities (never guessed from
 * keys or labels). Used for the submission's searchable columns and for matching.
 */
export function extractSubmitterIdentity(
	fields: ReadonlyArray<FormFieldDefinitionModel>,
	answers: Readonly<Record<string, FormAnswerValue>>,
): FormSubmitterIdentityModel {
	const byIdentity = (identity: string): string | null => {
		const field = fields.find((candidate) => candidate.identity === identity);
		if (field == null) {
			return null;
		}
		const value = answers[field.fieldKey];
		return typeof value === 'string' && value.length > 0 ? value : null;
	};

	const fullName = byIdentity('full_name');
	const joined = [byIdentity('first_name'), byIdentity('last_name')]
		.filter((part): part is string => part != null)
		.join(' ');

	return {
		name: fullName ?? (joined.length > 0 ? joined : null),
		email: byIdentity('email'),
		phone: byIdentity('phone'),
		company: byIdentity('company'),
	};
}
